import {create} from "@/store/createStore";
import type {TCompanyProfile} from "@/store/types";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

const CompanySchema = z.object({
  address_city: z
    .string({message: "company_profile:missing_address_city_error"})
    .min(1, "company_profile:missing_address_city_error"),
  address_street: z
    .string({message: "company_profile:missing_address_street_error"})
    .min(1, "company_profile:missing_address_street_error")
    .superRefine((val, ctx) => {
      if (val.length && !/\d/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "company_profile:missing_address_street_number_error",
        });
      }
    }),
  address_postal: z
    .string({message: "company_profile:missing_address_postal_error"})
    .min(1, "company_profile:missing_address_postal_error"),
  company_name: z.string().min(1, "company_profile:missing_company_name_error"),
  website: z.string().min(1, "company_profile:missing_website_error"),
  phonenumber: z.string().min(1, "company_profile:missing_phonenumber_error"),
});

type TCompanyProfileState = TCompanyProfile & {
  isPhoneNumberLengthCorrect: boolean;
  readonlyUsername: string;
  readonlyTikTokUsername: string | null;
};

// Admins will still use `company.user.id` for updating package subscriptions
type TState = {
  company: TCompanyProfileState;
  errors: Record<string, string> | null;
  setCompany(data: TCompanyProfile): void;
  setValue<K extends keyof NonNullable<TState["company"]>>(key: K, value: NonNullable<TState["company"]>[K]): void;
  personalInfoValidation(): boolean;
  reset(): void;
};

const companyInitialState = {} as TCompanyProfileState;

const useCompanyStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        company: companyInitialState,
        errors: null,
        setCompany: data => {
          const phoneNumber = data.phonenumber?.replaceAll(" ", "");

          return set(
            state => {
              state.company = {
                ...data,
                phonenumber: phoneNumber ?? "",
                isPhoneNumberLengthCorrect: true,
                readonlyUsername: data.username!,
                readonlyTikTokUsername: data.tiktok_username!,
              };

              if (!data.package_subscription?.package_type) {
                state.company.package_subscription = null;
              }
            },
            false,
            "companyStore/setCompany",
          );
        },
        setValue: (key, value) => {
          set(
            state => {
              state.company[key] = value;
            },
            false,
            "companyStore/setValue",
          );

          const errorKey = key.split(".").pop()!;

          if (!!get().errors && errorKey in get().errors! && !!get().errors![errorKey].length) {
            set(state => {
              delete state.errors![errorKey];
            });
          }
        },
        personalInfoValidation: () => {
          const result = CompanySchema.safeParse(get().company);

          if (!result.success) {
            result.error.issues.map(issue =>
              set(state => {
                if (state.errors) {
                  state.errors[issue.path[0]] = issue.message;
                } else {
                  state.errors = {[issue.path[0]]: issue.message};
                }
              }),
            );
          }

          return result.success;
        },
        reset: () =>
          set(
            state => {
              state.company = companyInitialState;
            },
            false,
            "companyStore/reset",
          ),
      }),
      {name: "companyStore"},
    ),
  ),
);

export default useCompanyStore;
